import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Prompt, useHistory, useLocation, useParams } from "react-router-dom";
import { ICategories, IDepartments, IProduct, IVariant } from "redux/reducers/models/product";
import { useAppDispatch } from "hooks/redux";
import Page from "components/page/Page";
import FormLayout from "components/form/FormLayout";
import Card from "components/card/Card";
import { IPrimaryPageAction } from "components/page/PageActions";
import "./newInventoryCount.scss";
import Portal from "elements/Portal";
import Popup from "components/popup/Popup";
import ReactDOM from "react-dom";
import DataTable from "../../../../../components/dataTable/DataTable";
import Input from "components/form/input";
import { ButtonNew as Button } from "components/buttonNew";
import { FontAwesomeIcon, FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleXmark } from "@fortawesome/pro-solid-svg-icons";
import { faTrashCanXmark } from "@fortawesome/pro-regular-svg-icons";
import axios from "axios";
import { StatusCode } from "api/protocols";
import { showError } from "redux/actions/ui";
import {
  DeleteInventoryCountVariants,
  PostInventoryCountVariants,
  TInventoryCount,
} from "api/rpc/2024-04/facilityAdmin/product/inventoryCount";
import Spin from "components/spin/spin";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { findSkuProduct, getProductDifferences } from "./inventoryHelpers";
import { isEmpty, valueToString } from "helpers/Helpers";
import { IconName } from "@fortawesome/fontawesome-svg-core";
import moment from "moment";
import { cloneDeep, isEqualWith, isNull, startCase } from "lodash";
import { getCountsBadgeType } from "./ViewInventoryCount";
import { Badge } from "components/badge/Badge";
import AddFolderProductsModal from "components/folders/AddFolderProductsModal";
import { defaultBrowseFilter, IBrowseFilter } from "components/folders/productBrowseFilters";
import { GetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import { GetDepartments } from "api/rpc/2024-04/facilityAdmin/facility/department";
import { appendUniqueProducts, buildSelectAllProductBatch, fetchFolderProductSearchPage, IFolderProductSearchContext } from "components/folders/folderProductSearch";

export type TSku = {
  sku: string;
  quantity: number;
  /**False value will display loader */
  searched: boolean;
};
interface IProductState {
  productsToAdd: Array<IProduct>;
  /**All selected products in state */
  selectedProducts: Array<IProduct>;
  /**The state of the products from the last save */
  lastSavedProducts: Array<IProduct>;
  skusNotFound: Array<TSku>;
  openModal: boolean;

  addedVariants: Array<Partial<IVariant>>;
  productSearchResults: IProduct[] | undefined;
  productSearchTotalCount: number | undefined;
  productSearchPending: boolean;
  productSearchLoadingMore: boolean;
  productSearchSelectAllPending: boolean;
}

interface ISkuState {
  skus: Array<TSku>;
  skuString: string;
}
interface ILocationState {
  params: { inventoryCount: TInventoryCount; products: Array<IProduct> };
}

interface IAutoSave {
  lastSaved: string;
  status: "Unsaved Changes" | "Saving Changes" | "Changes Saved";
  icon: IconName;
  iconProps: Omit<FontAwesomeIconProps, "icon">;
}

type TBrowseFilterState = {
  search: string;
  browseFilter: IBrowseFilter;
};

type TBrowseDepartment = IDepartments & { cateogries?: ICategories[] };

const ITEMS_SELECT_ALL_LIMIT = 200;
const INVENTORY_COUNT_START_TIME = 20;
const INVENTORY_COUNT_END_TIME = 0;

export default function NewInventoryCount() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const locationState = location.state as ILocationState;
  const { id } = useParams<{ id: string }>();
  const interval = useRef<NodeJS.Timeout>();
  const [productState, setProductState] = useState<IProductState>({
    productsToAdd: [],
    selectedProducts: [],
    lastSavedProducts: [],
    skusNotFound: [],
    openModal: false,

    addedVariants: [],
    productSearchResults: undefined,
    productSearchTotalCount: undefined,
    productSearchPending: false,
    productSearchLoadingMore: false,
    productSearchSelectAllPending: false,
  });
  const [skuState, setSkuState] = useState<ISkuState>({
    skus: [],
    skuString: "",
  });

  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  const [variantNotFoundState, setVariantNotFoundState] = useState<{ modalOpen: boolean; sku: string }>({
    modalOpen: false,
    sku: "",
  });

  const [autoSave, setAutoSave] = useState<IAutoSave>({
    lastSaved: null,
    status: "Unsaved Changes",
    icon: "circle-exclamation",
    iconProps: undefined,
  });

  const [browseFilterState, setBrowseFilterState] = useState<TBrowseFilterState>({
    search: "",
    browseFilter: defaultBrowseFilter,
  });

  const [browseDepartments, setBrowseDepartments] = useState<TBrowseDepartment[]>([]);
  const [browseMetadataLoading, setBrowseMetadataLoading] = useState(false);
  const [uncategorizedCount, setUncategorizedCount] = useState(0);
  const browseFilterRef = useRef(browseFilterState.browseFilter);
  const prevAddProductsModalOpen = useRef(false);

  const excludedVariantIds = useMemo(
    () => productState.addedVariants.map(variant => variant.id).filter((id): id is number => id != null),
    [productState.addedVariants],
  );
  const productSearchCancelTokenRef = useRef<ReturnType<typeof axios.CancelToken.source> | null>(null);
  const excludedVariantIdSet = useMemo(() => new Set(excludedVariantIds), [excludedVariantIds]);

  const [autoSaveTimer, setAutoSaveTimer] = useState<number>(INVENTORY_COUNT_START_TIME);

  const selectedProductsRef = useRef(productState.selectedProducts);
  const lastSavedProductsRef = useRef(productState.lastSavedProducts);

  useEffect(() => {
    selectedProductsRef.current = productState.selectedProducts;
    lastSavedProductsRef.current = productState.lastSavedProducts;
  }, [productState.selectedProducts, productState.lastSavedProducts]);

  useLayoutEffect(() => {
    const navigationEntries: any = window.performance.getEntriesByType("navigation");
    const isPageRefresh = navigationEntries.length > 0 && navigationEntries[0]?.type === "reload";
    if (isPageRefresh) {
      window.location.href = `/admin/settings/counts/view/${id}`;
    }
  }, [location, history]);

  useEffect(() => {
    //Show warning dialog box when browser is refreshed
    function handleBeforeUnload(event: BeforeUnloadEvent) {
      event.preventDefault();
      event.returnValue = "";
      return "";
    }
    window.addEventListener("beforeunload", handleBeforeUnload);

    //Check if previous inventory counted state exists. If so, set in state (when going from View Inventory Page to this page)
    const inventoryCountVariants = locationState?.params?.products;
    if (inventoryCountVariants) {
      //Set inventory variants from draft count
      setProductState(prevState => ({
        ...prevState,
        selectedProducts: inventoryCountVariants,
        lastSavedProducts: cloneDeep(inventoryCountVariants),
      }));
    }
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  //For searching variants by sku
  useEffect(() => {
    const source = axios.CancelToken.source();
    const currentSku = skuState?.skuString?.slice();
    const skuIndex = skuState?.skus?.findIndex(sku => sku.sku === currentSku);
    setSkuState(prevState => ({ ...prevState, skuString: "" }));
    if (skuIndex !== -1) {
      const foundSku = skuState.skus[skuIndex];
      // First sku added, search to see if variant exists
      if (foundSku.quantity === 1 || !foundSku?.searched) {
        void findSkuProduct(
          productState.selectedProducts,
          skuState.skus,
          setProductState,
          setSkuState,
          dispatch,
          source.token,
        );
      }
    }
    return () => {
      source.cancel();
    };
  }, [skuState?.skus]);

  useEffect(() => {
    const timer = setInterval(() => {
      setAutoSaveTimer(prev => {
        if (prev <= INVENTORY_COUNT_END_TIME) {
          return INVENTORY_COUNT_START_TIME;
        }
  
        return prev - 1;
      });
    }, 1000);
  
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (autoSaveTimer === INVENTORY_COUNT_END_TIME) {
      if (!isEqualWith(
        lastSavedProductsRef.current,
        selectedProductsRef.current,
        (originalProductArray, newProductArray) => {
          if (isNull(originalProductArray) && isNull(newProductArray)) {
            return true;
          }
        },
      )) {
        void handleSaveCount();
      }
    }
  }, [autoSaveTimer]);

  useEffect(() => {
    if (unsavedChangesExist()) {
      setAutoSave(prev => ({ ...prev, status: "Unsaved Changes", icon: "circle-exclamation" }));
    }
  }, [productState?.selectedProducts]);

  useEffect(() => {
    if (!productState.openModal) {
      productSearchCancelTokenRef.current?.cancel();
      setProductState(prevState => ({
        ...prevState,
        productSearchPending: false,
        productSearchLoadingMore: false,
      }));
      prevAddProductsModalOpen.current = false;
      return;
    }

    const justOpened = !prevAddProductsModalOpen.current;
    prevAddProductsModalOpen.current = true;

    const browseChanged =
      browseFilterRef.current.type !== browseFilterState.browseFilter.type ||
      browseFilterRef.current.id !== browseFilterState.browseFilter.id;
    browseFilterRef.current = browseFilterState.browseFilter;
    const delay = browseChanged ? 0 : 1000;

    let mounted = true;
    productSearchCancelTokenRef.current?.cancel();
    const cancelSource = axios.CancelToken.source();
    productSearchCancelTokenRef.current = cancelSource;

    setProductState(prevState => {
      const hasLoadedResults = (prevState.productSearchResults?.length ?? 0) > 0;

      return {
        ...prevState,
        productSearchPending: true,
        productSearchLoadingMore: false,
        ...(hasLoadedResults ? {} : { productSearchResults: undefined, productSearchTotalCount: undefined }),
      };
    });

    const runFetch = async () => {
      try {
        const { products, totalCount } = await fetchFolderProductSearchPage(
          GetProduct,
          getProductSearchContext(),
          0,
          true,
          cancelSource.token,
          () => dispatch(showError(t("Failed to get products"))),
        );
        if (mounted) {
          setProductState(prevState => ({
            ...prevState,
            productSearchResults: products,
            productSearchTotalCount: totalCount,
            productSearchPending: false,
            productSearchLoadingMore: false,
          }));
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          return;
        }
        if (mounted) {
          setProductState(prevState => ({
            ...prevState,
            productSearchResults: [],
            productSearchTotalCount: 0,
            productSearchPending: false,
            productSearchLoadingMore: false,
          }));
        }
      }
    };

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (justOpened) {
      void runFetch();
    } else {
      timeoutId = global.setTimeout(() => {
        void runFetch();
      }, delay);
    }

    return () => {
      mounted = false;
      cancelSource.cancel();
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [browseFilterState.search, browseFilterState.browseFilter, productState?.openModal]);

  function handleEnterKeydown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.code === "Enter" || e.code === "NumpadEnter") {
      void addSku();
    }
  }

  function unsavedChangesExist() {
    return !isEqualWith(
      productState.lastSavedProducts,
      productState.selectedProducts,
      (originalProductArray, newProductArray) => {
        if (isNull(originalProductArray) && isNull(newProductArray)) {
          return true;
        }
      },
    );
  }

  async function handleSaveCount() {
    const [productsToAdd, variantIdsToDelete] = getProductDifferences(
      [...productState.lastSavedProducts],
      [...productState.selectedProducts],
    );

    const variants: Array<IVariant> = [];
    productsToAdd?.forEach(product => {
      variants?.push(...[...product.variants]);
    });

    const formattedVariants = variants?.map(variant => ({
      variant_id: variant?.id,
      quantity_counted: variant?.quantity,
    }));

    const saveChanges = formattedVariants?.length > 0 || variantIdsToDelete?.length > 0;
    if (saveChanges) {
      //Show loader...
      setAutoSave({ status: "Saving Changes", icon: "spinner-third", iconProps: { spin: true }, lastSaved: null });
    }

    //Add/Update variant quantity counts
    if (formattedVariants?.length > 0) {
      const variantsRes = await PostInventoryCountVariants(
        { inventory_count_id: Number(id), variants: formattedVariants },
        false,
      );

      if (variantsRes?.status !== StatusCode.OK) {
        dispatch(showError(t("Error updating inventory counts")));
        return;
      }
    }

    //Remove variants that were deleted from the last save
    if (variantIdsToDelete?.length > 0) {
      const deleteRes = await DeleteInventoryCountVariants(
        { inventory_count_id: Number(id), variants: variantIdsToDelete },
        false,
      );
      if (deleteRes?.status !== StatusCode.OK) {
        dispatch(showError(t("Error removing variants from count")));
        return;
      }
    }

    if (saveChanges) {
      setAutoSave({
        status: "Changes Saved",
        icon: "circle-check",
        iconProps: undefined,
        lastSaved: `Last saved: ${moment().format("LT")}`,
      });
      setSaveSuccess(true);
    }

    ReactDOM.unstable_batchedUpdates(() => {
      setProductState(prevState => ({ ...prevState, lastSavedProducts: cloneDeep(prevState?.selectedProducts) }));
      //Reset 20 second timer
      global.clearInterval(interval.current);
      setAutoSaveTimer(INVENTORY_COUNT_START_TIME);
    });
  }

  function addSku() {
    if (skuState.skuString) {
      const updatedSkus = [...skuState.skus];
      const productIncremented = incrementProductQuantity(skuState.skuString);
      if (!productIncremented) {
        const foundSkuIndex = updatedSkus?.findIndex(skuObj => skuObj.sku === skuState.skuString);
        if (foundSkuIndex !== -1) {
          //Increment quantity of sku found
          const foundSku = updatedSkus[foundSkuIndex];
          updatedSkus.splice(foundSkuIndex, 1, { ...foundSku, quantity: ++foundSku.quantity });
        } else {
          //Add new sku
          const newSku: TSku = { sku: skuState.skuString, quantity: 1, searched: false };
          updatedSkus.push(newSku);
        }
        setSkuState(prevState => ({ ...prevState, skus: updatedSkus }));
      } else {
        setSkuState(prevState => ({ ...prevState, skuString: "" }));
      }
    }
  }

  function incrementProductQuantity(currentSku: string) {
    const products = [...productState.selectedProducts];
    let productInList = false;
    let variantIndex: number = null;
    const productIndex = products?.findIndex(product => {
      const foundVariantIndex = product?.variants?.findIndex(
        variant => variant?.sku === currentSku || variant?.barcode === currentSku,
      );
      if (foundVariantIndex !== -1) {
        variantIndex = foundVariantIndex;
        return true;
      }
    });
    if (productIndex !== -1 && variantIndex !== -1) {
      // Product already in list, increment the quantity
      const foundVariant = products[productIndex]?.variants[variantIndex];
      products[productIndex].variants[variantIndex] = { ...foundVariant, quantity: ++foundVariant.quantity };
      productInList = true;
      setProductState(prevState => ({ ...prevState, selectedProducts: products }));
    }
    return productInList;
  }

  function handleProductSelect(products: Array<IProduct>) {
    const selectedProducts = withDefaultCountedQuantity(products);
    const { updatedSkus, updatedProducts } = updateSkus(selectedProducts);
    ReactDOM.unstable_batchedUpdates(() => {
      setProductState(prevState => ({ ...prevState, selectedProducts: updatedProducts, openModal: false }));
      setSkuState(prevState => ({ ...prevState, skus: updatedSkus }));
      setBrowseFilterState({ search: "", browseFilter: defaultBrowseFilter });
    });
  }

  function updateSkus(products: Array<IProduct>) {
    const updatedSkus = [...skuState.skus];
    const updatedProducts = [...products];
    updatedProducts?.forEach((product, productIndex) => {
      product?.variants?.forEach((variant, variantIndex) => {
        const skuIndex = updatedSkus?.findIndex(sku => sku.sku === variant?.sku || sku.sku === variant?.barcode);
        if (skuIndex !== -1) {
          updatedProducts[productIndex].variants[variantIndex] = {
            ...variant,
            quantity: Number(variant.quantity) + Number(updatedSkus[skuIndex]?.quantity),
          };
          updatedSkus.splice(skuIndex, 1);
        }
      });
    });
    return { updatedSkus: updatedSkus, updatedProducts: updatedProducts };
  }

  function handleEnterClick() {
    const skuSearchId = document.getElementById("skuSearchId");
    skuSearchId.focus();
    void addSku();
  }

  function removeSku(index: number) {
    const updatedSkus = [...skuState.skus];
    // Sku cannot be removed if search is in progress
    const allSkusSearched = updatedSkus?.every(sku => sku.searched);
    if (updatedSkus[index] && allSkusSearched) {
      updatedSkus.splice(index, 1);
      setSkuState(prevState => ({ ...prevState, skus: updatedSkus }));
    }
  }

  function removeVariant(productIndex: number, variantIndex: number) {
    const updatedProducts = [...productState.selectedProducts];
    //Product with single variant
    if (variantIndex == null || updatedProducts[productIndex]?.variants?.length === 1) {
      updatedProducts.splice(productIndex, 1);
    } else {
      // Product with multiple variants
      if (updatedProducts[productIndex]?.variants[variantIndex]) {
        updatedProducts[productIndex]?.variants?.splice(variantIndex, 1);
      }
    }

    setProductState(prevState => ({ ...prevState, selectedProducts: updatedProducts }));
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>, productIndex: number, variantIndex: number) {
    const { valueAsNumber } = e.target;
    const updatedProducts = [...productState.selectedProducts];
    const updatedVariant = updatedProducts[productIndex].variants[variantIndex];
    updatedProducts[productIndex].variants[variantIndex] = {
      ...updatedVariant,
      quantity: isNaN(valueAsNumber) ? null : valueAsNumber,
    };
    setProductState(prevState => ({ ...prevState, selectedProducts: updatedProducts }));
  }

  function quantityOnBlur(productIndex: number, variantIndex: number, quantity: number) {
    const currentQuantity = Number(quantity);
    //Set the quantity to '0' if the entered value is empty or negative
    if (currentQuantity < 0 || isEmpty(quantity)) {
      const updatedProducts = [...productState.selectedProducts];
      const updatedVariant = updatedProducts[productIndex].variants[variantIndex];
      updatedProducts[productIndex].variants[variantIndex] = {
        ...updatedVariant,
        quantity: 0,
      };
      setProductState(prevState => ({ ...prevState, selectedProducts: updatedProducts }));
    }
  }

  const saveAction: IPrimaryPageAction = {
    content: t("Save"),
    action: () => void handleSaveCount(),
  };

  if ((window.performance.getEntriesByType("navigation")[0] as any)?.type === "reload") {
    return null;
  }

  function handleBrowseProductsVisibility() {
    const opening = !productState.openModal;

    setProductState(prevState => ({
      ...prevState,
      openModal: opening,
      productsToAdd: [...prevState.selectedProducts],
      ...(opening
        ? {
          productSearchPending: true,
          productSearchResults: undefined,
          productSearchTotalCount: undefined,
        }
        : {}),
    }));

    if (opening) {
      setBrowseFilterState({ search: "", browseFilter: defaultBrowseFilter });
      void loadBrowseMetadata();
    } else {
      setBrowseFilterState({ search: "", browseFilter: defaultBrowseFilter });
    }
  }

  async function loadBrowseMetadata() {
    const showLoading = browseDepartments.length === 0;

    if (showLoading) {
      setBrowseMetadataLoading(true);
    }

    try {
      const [departmentRes, countRes] = await Promise.all([
        GetDepartments({ extended: true }, false),
        GetProduct(
          {
            get_count: true,
            limit: 1,
            filters: [{ label: "uncategorized", value: [1] }],
          },
          false,
        ),
      ]);

      if (departmentRes.status === StatusCode.OK) {
        setBrowseDepartments((departmentRes.data ?? []).filter(department => department.type === "department"));
      }

      if (countRes.status === StatusCode.OK && countRes.data && !Array.isArray(countRes.data)) {
        const countData = countRes.data as { count?: number };
        setUncategorizedCount(countData.count ?? 0);
      }
    } finally {
      if (showLoading) {
        setBrowseMetadataLoading(false);
      }
    }
  }

  function handleAddProductsBrowseSearchChange(value: string) {
    setBrowseFilterState(prevState => ({ ...prevState, search: value }));
    setProductState(prev => ({ ...prev, productSearchPending: true }));
  }

  function handleAddProductsBrowseFilterChange(browseFilter: IBrowseFilter) {
    setBrowseFilterState(prevState => ({ ...prevState, browseFilter }));
    setProductState(prev => ({ ...prev, productSearchPending: true }));
  }

  function getProductSearchContext(): IFolderProductSearchContext {
    return {
      search: browseFilterState.search,
      browseFilter: browseFilterState.browseFilter,
      variants: true,
      track_inventory: true,
    };
  }

  async function loadMoreProducts() {
    let shouldLoad = false;
    let offset = 0;

    setProductState(prev => {
      if (
        prev.productSearchLoadingMore ||
        prev.productSearchPending ||
        !prev.productSearchResults ||
        prev.productSearchTotalCount == null ||
        prev.productSearchResults.length >= prev.productSearchTotalCount
      ) {
        return prev;
      }

      shouldLoad = true;
      offset = prev.productSearchResults.length;
      return { ...prev, productSearchLoadingMore: true };
    });

    if (!shouldLoad) {
      return;
    }

    try {
      const { products } = await fetchFolderProductSearchPage(
        GetProduct,
        getProductSearchContext(),
        offset,
        false,
        undefined,
        () => dispatch(showError(t("Failed to get products"))),
      );
      setProductState(prev => ({
        ...prev,
        productSearchResults: appendUniqueProducts(prev.productSearchResults ?? [], products),
        productSearchLoadingMore: false,
      }));
    } catch {
      setProductState(prev => ({ ...prev, productSearchLoadingMore: false }));
    }
  }

  async function selectAllMatchingProducts() {
    const loaded = productState.productSearchResults ?? [];
    const totalCount = productState.productSearchTotalCount ?? loaded.length;
    const fullyAddedIds = new Set(
      loaded.filter(product => withSelectableVariants(product) == null).map(product => product.id),
    );
    const fullySelectedIds = new Set(
      productState.productsToAdd
        .filter(selected => {
          const source = loaded.find(product => product.id === selected.id) ?? selected;
          const available = withSelectableVariants(source);
          if (available == null) {
            return true;
          }

          const selectedVariantIds = new Set((selected.variants ?? []).map(variant => variant.id));
          return available.variants.every(variant => selectedVariantIds.has(variant.id));
        })
        .map(product => product.id),
    );
    const excludeIds = new Set<number>([...fullyAddedIds, ...fullySelectedIds]);
    const remaining = Math.max(0, ITEMS_SELECT_ALL_LIMIT - productState.productsToAdd.length);
    const needsRemoteFetch = remaining > 0 && loaded.length < totalCount;

    if (needsRemoteFetch) {
      setProductState(prev => ({ ...prev, productSearchSelectAllPending: true }));
    }

    try {
      const toAdd = await buildSelectAllProductBatch(
        GetProduct,
        getProductSearchContext(),
        loaded,
        totalCount,
        excludeIds,
        remaining,
        () => dispatch(showError(t("Failed to get products"))),
      );

      const selectable = toAdd
        .map(product => withSelectableVariants(product))
        .filter((product): product is IProduct => product != null);

      setProductState(prev => {
        if (selectable.length === 0) {
          return { ...prev, productSearchSelectAllPending: false };
        }

        return {
          ...prev,
          productsToAdd: mergeSelectedProducts(prev.productsToAdd, selectable),
          productSearchSelectAllPending: false,
        };
      });
    } catch {
      setProductState(prev => ({ ...prev, productSearchSelectAllPending: false }));
    }
  }

  function mergeSelectedProducts(existing: IProduct[], incoming: IProduct[]) {
    const next = [...existing];

    incoming.forEach(product => {
      const index = next.findIndex(selected => selected.id === product.id);
      if (index === -1) {
        next.push(product);
        return;
      }

      const existingVariantIds = new Set((next[index].variants ?? []).map(variant => variant.id));
      const mergedVariants = [...(next[index].variants ?? [])];
      (product.variants ?? []).forEach(variant => {
        if (!existingVariantIds.has(variant.id)) {
          mergedVariants.push(variant);
          existingVariantIds.add(variant.id);
        }
      });
      next[index] = { ...next[index], variants: mergedVariants, variant_count: mergedVariants.length };
    });

    return next;
  }

  function withSelectableVariants(product: IProduct): IProduct | null {
    const variants = (product.variants ?? []).filter(variant => !excludedVariantIdSet.has(variant.id));
    if (variants.length === 0) {
      return null;
    }

    return {
      ...product,
      variants,
      variant_count: variants.length,
    };
  }

  function handleSelectProductToAdd(productOrProducts: IProduct | IProduct[]) {
    const products = Array.isArray(productOrProducts) ? productOrProducts : [productOrProducts];
    const selectable = products
      .map(product => withSelectableVariants(product))
      .filter((product): product is IProduct => product != null);

    if (selectable.length === 0) {
      return;
    }

    setProductState(prev => ({
      ...prev,
      productsToAdd: mergeSelectedProducts(prev.productsToAdd, selectable),
    }));
  }

  function handleUnselectProductToAdd(productOrProducts: IProduct | IProduct[]) {
    const products = Array.isArray(productOrProducts) ? productOrProducts : [productOrProducts];

    setProductState(prev => {
      let next = [...prev.productsToAdd];

      products.forEach(incoming => {
        const index = next.findIndex(product => product.id === incoming.id);
        if (index === -1) {
          return;
        }

        const removeIds = new Set((incoming.variants ?? []).map(variant => variant.id));
        if (removeIds.size === 0) {
          next = next.filter(product => product.id !== incoming.id);
          return;
        }

        const remaining = (next[index].variants ?? []).filter(variant => !removeIds.has(variant.id));
        if (remaining.length === 0) {
          next = next.filter(product => product.id !== incoming.id);
        } else {
          next[index] = { ...next[index], variants: remaining, variant_count: remaining.length };
        }
      });

      return { ...prev, productsToAdd: next };
    });
  }

  function withDefaultCountedQuantity(products: IProduct[]): IProduct[] {
    return products.map(product => ({
      ...product,
      variants: (product.variants ?? []).map(variant => ({
        ...variant,
        quantity: variant.quantity ?? 1,
      })),
    }));
  }

  const count = locationState?.params?.inventoryCount;

  return (
    <Page
      title={t("Count Inventory")}
      titleMetadata={<div className="space-x-2">
        <Badge size="medium" type={getCountsBadgeType(count?.status)}>
          {valueToString(count?.status)}
        </Badge>
        <Badge size="medium" type={count?.scope === 'partial' ? 'gray' : 'lightblue'}>
          {t(startCase(count?.scope) + ' Inventory Count')}
        </Badge>
      </div>}
      primaryAction={saveAction}
      breadcrumbs={[
        saveSuccess || locationState?.params?.products
          ? { prefix: true, label: t("Back to View Count"), url: `/admin/settings/counts/view/${id}` }
          : { prefix: true, label: t("Back to Inventory Counts"), url: "/admin/settings/counts" },
      ]}
      actionDetails={{
        icon: autoSave.icon,
        content: autoSave.status,
        toolTipContent: autoSave.lastSaved,
        iconProps: autoSave.iconProps,
      }}
    >
      <Card>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <Input
                id="skuSearchId"
                onKeyDown={handleEnterKeydown}
                value={skuState.skuString}
                placeholder={t("Scan Sku / Barcode")}
                onChange={e => setSkuState(prevState => ({ ...prevState, skuString: e.target.value }))}
                trailingButtons={[
                  <Button key={1} onClick={handleEnterClick} type="secondary">
                    {t('Enter')}
                  </Button>,
                  <Button
                    key={2}
                    type="primary"
                    size="medium"
                    icon={<FontAwesomeIcon className="ml-2" icon={["far", "magnifying-glass"]} />}
                    onClick={() => handleBrowseProductsVisibility()}
                  >
                    {t('Search')}
                  </Button>,
                ]}
              />
            </FormLayout.Group>
          </FormLayout>
          <DataTable
            columns={[
              ...(productState?.selectedProducts?.length > 0 ? [{ label: t("Title") }, { label: t("Vendor") }] : []),
              ...(productState?.selectedProducts?.length > 0
                ? [{ label: t("Sku") }, { label: t("Barcode") }]
                : [{ label: t("Sku / Barcode") }]),
              {
                label: t("Quantity"),
              },
              { label: "" },
              { label: "" },
            ]}
          >
            {skuState?.skus?.map((sku, index) => {
              return (
                <tr key={sku.sku}>
                  {productState?.selectedProducts?.length > 0 && (
                    <>
                      <td></td>
                      <td></td>
                      <td>{sku?.sku}</td>
                    </>
                  )}
                  <td>{sku?.sku}</td>
                  <td>{sku?.quantity}</td>
                  <td className="text-right">
                    {!sku.searched ? (
                      <div style={{ display: "inline-flex" }}>
                        <Spin />
                      </div>
                    ) : (
                      <OverlayTrigger
                        placement="auto"
                        overlay={<Tooltip id="headingTooltip">{t('Product not found')}</Tooltip>}
                      >
                        <FontAwesomeIcon
                          className="inventory-counts-error"
                          size="1x"
                          icon={faCircleXmark}
                          onClick={() => setVariantNotFoundState({ modalOpen: true, sku: sku.sku })}
                        />
                      </OverlayTrigger>
                    )}
                  </td>
                  <td className="text-right">
                    <FontAwesomeIcon
                      onClick={() => removeSku(index)}
                      icon={faTrashCanXmark}
                      size="1x"
                      className="cursor-pointer"
                    />
                  </td>
                </tr>
              );
            })}
            {productState?.selectedProducts?.map((product, productIndex) => {
              const singleVariant = product?.variants?.length === 1 && product?.title === product?.variants[0]?.title;
              return (
                <React.Fragment key={product.id}>
                  <tr className="inventory-counts-product-header">
                    <td>{product?.preferred_title ? product?.preferred_title : product.title}</td>
                    <td>{product.vendor_title}</td>
                    <td>{singleVariant && product?.variants[0]?.sku}</td>
                    <td>{singleVariant && product?.variants[0]?.barcode}</td>
                    <td style={{ width: "15%" }} onClick={e => e.stopPropagation()}>
                      {singleVariant && (
                        <Input
                          type="number"
                          labelHidden
                          onChange={e => handleInputChange(e, productIndex, 0)}
                          value={product?.variants[0]?.quantity ?? ""}
                          placeholder={t("Quantity")}
                          onBlur={() => quantityOnBlur(productIndex, 0, product?.variants[0]?.quantity)}
                          error={isEmpty(product?.variants[0]?.quantity)}
                        />
                      )}
                    </td>
                    <td className="text-right">
                      {singleVariant && (
                        <OverlayTrigger placement="auto" overlay={<Tooltip id="headingTooltip">{t('Product found')}</Tooltip>}>
                          <FontAwesomeIcon className="inventory-counts-success" size="1x" icon={faCircleCheck} />
                        </OverlayTrigger>
                      )}
                    </td>
                    <td className="text-right">
                      {singleVariant ? (
                        <FontAwesomeIcon
                          onClick={() => removeVariant(productIndex, null)}
                          icon={faTrashCanXmark}
                          size="1x"
                          className="cursor-pointer"
                        />
                      ) : null}
                    </td>
                  </tr>
                  {!singleVariant &&
                    product?.variants?.map((variant, variantIndex) => (
                      <tr key={variant.id}>
                        <td style={{ textIndent: "3rem" }}>{variant?.title}</td>
                        <td></td>
                        <td>{variant?.sku}</td>
                        <td>{variant?.barcode}</td>
                        <td style={{ width: "15%" }} onClick={e => e.stopPropagation()}>
                          <Input
                            type="number"
                            labelHidden
                            onChange={e => handleInputChange(e, productIndex, variantIndex)}
                            value={variant?.quantity ?? ""}
                            placeholder={t("Quantity")}
                            onBlur={() => quantityOnBlur(productIndex, variantIndex, variant?.quantity)}
                            error={isEmpty(variant?.quantity)}
                          />
                        </td>
                        <td className="text-right">
                          {
                            <OverlayTrigger
                              placement="auto"
                              overlay={<Tooltip id="headingTooltip">{t('Product found')}</Tooltip>}
                            >
                              <FontAwesomeIcon className="inventory-counts-success" size="1x" icon={faCircleCheck} />
                            </OverlayTrigger>
                          }
                        </td>
                        <td className="text-right">
                          <FontAwesomeIcon
                            onClick={() => removeVariant(productIndex, variantIndex)}
                            icon={faTrashCanXmark}
                            size="1x"
                            className="cursor-pointer"
                          />
                        </td>
                      </tr>
                    ))}
                </React.Fragment>
              );
            })}
          </DataTable>
        </Card.Section>
      </Card>

      <Portal isMounted={variantNotFoundState.modalOpen}>
        <Popup
          open={variantNotFoundState.modalOpen}
          type="warning"
          title={t("Product Not Found")}
          onOk={() => setVariantNotFoundState({ modalOpen: false, sku: "" })}
          okText={t("Close")}
          description=""
          closable
          backDropCancel
        >
          <p className="mb-2">
            A product corresponding to SKU or barcode {<b>{variantNotFoundState.sku}</b>} was not found.
          </p>
          <p className="mb-2">{t('Please proceed to the products page to update a product with this SKU / barcode.')}</p>
        </Popup>
      </Portal>

      <AddFolderProductsModal
        open={productState.openModal}
        onClose={handleBrowseProductsVisibility}
        onAdd={handleProductSelect}
        search={browseFilterState.search}
        onSearchChange={handleAddProductsBrowseSearchChange}
        browseFilter={browseFilterState.browseFilter}
        onBrowseFilterChange={handleAddProductsBrowseFilterChange}
        departments={browseDepartments}
        departmentsLoading={browseMetadataLoading}
        uncategorizedCount={uncategorizedCount}
        productsToAdd={productState.productsToAdd}
        availableSlotCount={ITEMS_SELECT_ALL_LIMIT}
        totalEmptySlotCount={ITEMS_SELECT_ALL_LIMIT}
        folderProducts={[]}
        productSearchResults={productState.productSearchResults}
        productSearchTotalCount={productState.productSearchTotalCount}
        productSearchPending={productState.productSearchPending}
        productSearchLoadingMore={productState.productSearchLoadingMore}
        productSearchSelectAllPending={productState.productSearchSelectAllPending}
        onLoadMoreProducts={loadMoreProducts}
        onSelectAllMatchingProducts={selectAllMatchingProducts}
        onSelectProduct={handleSelectProductToAdd}
        onUnselectProduct={handleUnselectProductToAdd}
        hideSlots
        showQuantity
        enableVariantSelection
        excludedVariantIds={excludedVariantIds}
        title={t("Browse products")}
        supportText={t("Add items to this purchase order")}
        onCreateProduct={() => {
          void handleBrowseProductsVisibility();
          history.push("/admin/product/new");
        }}
        onGoToProducts={() => {
          void handleBrowseProductsVisibility();
          history.push("/admin/product");
        }}
        browseOnOpen
      />

      <Prompt
        when={!saveSuccess || unsavedChangesExist()}
        message={() => "Unsaved changes will be lost if you leave the page. Continue?"}
      />
    </Page>
  );
}
